<template>
  <BaseDialog :open="open" :labelled-by="titleId" panel-class="max-w-sm rounded-lg p-6" @close="$emit('cancel')">
    <h3 :id="titleId" class="text-lg font-semibold text-text">{{ title }}</h3>
    <p class="mt-2 text-sm text-muted">{{ message }}</p>

    <FormField
      v-if="typeToConfirm"
      v-model="typed"
      :label="`Type ${typeToConfirm} to confirm`"
      autocomplete="off"
      autofocus
      :reserve-error-space="false"
      class="mt-4"
    />
    <p v-if="error" class="mt-3 text-xs text-error">{{ error }}</p>

    <div class="mt-6 flex justify-end gap-3">
      <button
        type="button"
        :autofocus="!typeToConfirm"
        :disabled="busy"
        class="px-4 py-2 text-sm font-medium text-text bg-bg border border-border rounded-lg hover:bg-canvas disabled:opacity-50 transition-colors"
        @click="$emit('cancel')"
      >
        Cancel
      </button>
      <button
        type="button"
        :disabled="busy || !typedMatches"
        class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-error rounded-lg hover:bg-error/85 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        @click="$emit('confirm')"
      >
        <Loader2 v-if="busy" :size="14" class="animate-spin" />
        {{ confirmLabel ?? 'Confirm' }}
      </button>
    </div>
  </BaseDialog>
</template>

<script setup lang="ts">
// Destructive confirmation. Cancel takes focus by default (the least
// destructive key is Enter). `typeToConfirm` adds the "type DELETE" gate.
import { computed, ref, useId, watch } from 'vue'
import { Loader2 } from 'lucide-vue-next'
import BaseDialog from '@/components/BaseDialog.vue'
import FormField from '@/components/FormField.vue'

const props = defineProps<{
  open: boolean
  title: string
  message: string
  confirmLabel?: string
  /** Word the user must type before Confirm enables. */
  typeToConfirm?: string
  busy?: boolean
  error?: string
}>()

defineEmits<{
  confirm: []
  cancel: []
}>()

const titleId = useId()
const typed = ref('')
const typedMatches = computed(() => !props.typeToConfirm || typed.value === props.typeToConfirm)

watch(
  () => props.open,
  () => {
    typed.value = ''
  },
)
</script>
